import React, { useState, useEffect } from 'react';
import { Settings, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import { SystemSettings, SystemSettingsUpdate } from '../../types/system-settings';
import { getSystemSettings, updateSystemSettings } from '../../api/system-settings';

/** `regional_preferences` is a free-form dict in the API; only strings are usable here. */
const preferenceString = (value: unknown, fallback: string): string =>
    typeof value === 'string' && value ? value : fallback;

/**
 * ApplicationSettingsPage - Configure application-wide settings
 * 
 * Feature: System Administration Interface (001-admin-settings)
 * User Story: US4 - Configure Application Settings (Priority P2)
 * 
 * Features:
 * - Configure site name (application display name)
 * - Set default timezone (IANA timezone identifier)
 * - Set default language (ISO language code)
 * - Regional preferences (date format, number format, currency)
 * - Admin-only access with immediate effect on save
 */
const ApplicationSettingsPage: React.FC = () => {
    const [settings, setSettings] = useState<SystemSettings | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    
    // Form state
    const [siteName, setSiteName] = useState('');
    const [defaultTimezone, setDefaultTimezone] = useState('');
    const [defaultLanguage, setDefaultLanguage] = useState('');
    const [dateFormat, setDateFormat] = useState('YYYY-MM-DD');
    const [numberFormat, setNumberFormat] = useState('en-US');
    const [currency, setCurrency] = useState('USD');

    // Common IANA timezone identifiers
    const timezones = [
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'America/Phoenix',
        'America/Anchorage',
        'America/Honolulu',
        'America/Toronto',
        'America/Vancouver',
        'America/Mexico_City',
        'Europe/London',
        'Europe/Paris',
        'Europe/Berlin',
        'Europe/Madrid',
        'Europe/Rome',
        'Europe/Amsterdam',
        'Europe/Brussels',
        'Europe/Vienna',
        'Europe/Stockholm',
        'Europe/Oslo',
        'Europe/Copenhagen',
        'Europe/Helsinki',
        'Europe/Warsaw',
        'Europe/Prague',
        'Europe/Budapest',
        'Europe/Athens',
        'Europe/Istanbul',
        'Europe/Moscow',
        'Asia/Dubai',
        'Asia/Kolkata',
        'Asia/Bangkok',
        'Asia/Singapore',
        'Asia/Hong_Kong',
        'Asia/Shanghai',
        'Asia/Tokyo',
        'Asia/Seoul',
        'Australia/Sydney',
        'Australia/Melbourne',
        'Australia/Brisbane',
        'Australia/Perth',
        'Pacific/Auckland',
        'Africa/Cairo',
        'Africa/Johannesburg',
        'Africa/Lagos',
        'Africa/Nairobi'
    ];

    // Supported languages
    const languages = [
        { code: 'en', name: 'English' },
        { code: 'es', name: 'Spanish (Español)' },
        { code: 'fr', name: 'French (Français)' },
        { code: 'de', name: 'German (Deutsch)' },
        { code: 'pt', name: 'Portuguese (Português)' }
    ];

    // Date format options
    const dateFormats = [
        { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-01-26)' },
        { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (26/01/2026)' },
        { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (01/26/2026)' },
        { value: 'DD.MM.YYYY', label: 'DD.MM.YYYY (26.01.2026)' }
    ];

    // Number format options (locale codes)
    const numberFormats = [
        { value: 'en-US', label: 'US (1,234.56)' },
        { value: 'en-GB', label: 'UK (1,234.56)' },
        { value: 'de-DE', label: 'German (1.234,56)' },
        { value: 'fr-FR', label: 'French (1 234,56)' },
        { value: 'es-ES', label: 'Spanish (1.234,56)' }
    ];

    // Currency options
    const currencies = [
        { code: 'USD', name: 'US Dollar ($)' },
        { code: 'EUR', name: 'Euro (€)' },
        { code: 'GBP', name: 'British Pound (£)' },
        { code: 'JPY', name: 'Japanese Yen (¥)' },
        { code: 'CAD', name: 'Canadian Dollar (C$)' },
        { code: 'AUD', name: 'Australian Dollar (A$)' },
        { code: 'CHF', name: 'Swiss Franc (Fr)' },
        { code: 'CNY', name: 'Chinese Yuan (¥)' },
        { code: 'INR', name: 'Indian Rupee (₹)' },
        { code: 'ZAR', name: 'South African Rand (R)' }
    ];

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await getSystemSettings();
            setSettings(data);
            
            // Populate form fields
            setSiteName(data.site_name);
            setDefaultTimezone(data.default_timezone);
            setDefaultLanguage(data.default_language);
            
            // Load regional preferences
            if (data.regional_preferences) {
                setDateFormat(preferenceString(data.regional_preferences.date_format, 'YYYY-MM-DD'));
                setNumberFormat(preferenceString(data.regional_preferences.number_format, 'en-US'));
                setCurrency(preferenceString(data.regional_preferences.currency, 'USD'));
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load settings';
            setError(message);
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validation
        if (!siteName.trim()) {
            toast.error('Site name is required');
            return;
        }
        
        if (siteName.length > 255) {
            toast.error('Site name must be 255 characters or less');
            return;
        }

        setIsSaving(true);
        try {
            const updates: SystemSettingsUpdate = {
                site_name: siteName.trim(),
                default_timezone: defaultTimezone,
                default_language: defaultLanguage,
                regional_preferences: {
                    date_format: dateFormat,
                    number_format: numberFormat,
                    currency: currency
                }
            };

            const updated = await updateSystemSettings(updates);
            setSettings(updated);
            toast.success('Application settings updated successfully');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update settings';
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleReset = () => {
        if (settings) {
            setSiteName(settings.site_name);
            setDefaultTimezone(settings.default_timezone);
            setDefaultLanguage(settings.default_language);
            
            if (settings.regional_preferences) {
                setDateFormat(preferenceString(settings.regional_preferences.date_format, 'YYYY-MM-DD'));
                setNumberFormat(preferenceString(settings.regional_preferences.number_format, 'en-US'));
                setCurrency(preferenceString(settings.regional_preferences.currency, 'USD'));
            }
            
            toast.success('Form reset to current settings');
        }
    };

    if (isLoading) {
        return (
            <div className="loading-container min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    if (error && !settings) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Card variant="elevated">
                    <Card.Body>
                        <div className="text-center py-12">
                            <p className="text-error text-lg mb-4">{error}</p>
                            <Button variant="primary" onClick={loadSettings}>
                                Retry
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <Settings className="w-8 h-8 text-primary-600" />
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Application Settings
                    </h1>
                </div>
                <p className="text-gray-600 dark:text-gray-400">
                    Configure application-wide settings that apply to all users
                </p>
            </div>

            {/* Settings Form */}
            <form onSubmit={handleSubmit}>
                {/* General Settings Section */}
                <Card variant="elevated" className="mb-6">
                    <Card.Header
                        title="General Settings"
                        subtitle="Application display name and branding"
                    />
                    <Card.Body>
                        <div className="space-y-4">
                            {/* Site Name */}
                            <Input
                                type="text"
                                id="site_name"
                                label="Site Name"
                                required
                                value={siteName}
                                onChange={(e) => setSiteName(e.target.value)}
                                maxLength={255}
                                placeholder="e.g., Project Management - Akhile"
                                helperText="Displayed in the application header and browser title (1-255 characters)"
                            />
                        </div>
                    </Card.Body>
                </Card>

                {/* Localization Settings Section */}
                <Card variant="elevated" className="mb-6">
                    <Card.Header
                        title="Localization Settings"
                        subtitle="Timezone, language, and regional preferences"
                    />
                    <Card.Body>
                        <div className="space-y-4">
                            {/* Default Timezone */}
                            <Select
                                id="default_timezone"
                                label="Default Timezone"
                                required
                                value={defaultTimezone}
                                onChange={(e) => setDefaultTimezone(e.target.value)}
                                helperText="IANA timezone identifier for new users (e.g., America/New_York)"
                            >
                                {timezones.map((tz) => (
                                    <option key={tz} value={tz}>
                                        {tz}
                                    </option>
                                ))}
                            </Select>

                            {/* Default Language */}
                            <Select
                                id="default_language"
                                label="Default Language"
                                required
                                value={defaultLanguage}
                                onChange={(e) => setDefaultLanguage(e.target.value)}
                                helperText="ISO language code for application interface"
                            >
                                {languages.map((lang) => (
                                    <option key={lang.code} value={lang.code}>
                                        {lang.name}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    </Card.Body>
                </Card>

                {/* Regional Preferences Section */}
                <Card variant="elevated" className="mb-6">
                    <Card.Header
                        title="Regional Preferences"
                        subtitle="Date format, number format, and currency"
                    />
                    <Card.Body>
                        <div className="space-y-4">
                            {/* Date Format */}
                            <Select
                                id="date_format"
                                label="Date Format"
                                value={dateFormat}
                                onChange={(e) => setDateFormat(e.target.value)}
                                helperText="How dates are displayed throughout the application"
                            >
                                {dateFormats.map((format) => (
                                    <option key={format.value} value={format.value}>
                                        {format.label}
                                    </option>
                                ))}
                            </Select>

                            {/* Number Format */}
                            <Select
                                id="number_format"
                                label="Number Format"
                                value={numberFormat}
                                onChange={(e) => setNumberFormat(e.target.value)}
                                helperText="Thousands separator and decimal format"
                            >
                                {numberFormats.map((format) => (
                                    <option key={format.value} value={format.value}>
                                        {format.label}
                                    </option>
                                ))}
                            </Select>

                            {/* Currency */}
                            <Select
                                id="currency"
                                label="Currency"
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value)}
                                helperText="Default currency for financial data and reports"
                            >
                                {currencies.map((curr) => (
                                    <option key={curr.code} value={curr.code}>
                                        {curr.name}
                                    </option>
                                ))}
                            </Select>
                        </div>
                    </Card.Body>
                </Card>

                {/* Form Actions */}
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleReset}
                        disabled={isSaving}
                    >
                        Reset
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        isLoading={isSaving}
                        leftIcon={Save}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </form>

            {/* Last Updated Info */}
            {settings && (
                <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    Last updated: {new Date(settings.updated_at).toLocaleString()}
                </div>
            )}
        </div>
    );
};

export default ApplicationSettingsPage;
